import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, CircleMinus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import {
  ResourceList,
  type ResourceColumn,
  type ResourceFilterDef,
} from '@/components/resources/ResourceList';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import type { ListQueryConfig } from '@/lib/listQuery';
import { useListQuery } from '@/lib/hooks/useListQuery';
import { usePersistedColumns } from '@/lib/hooks/usePersistedColumns';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useServerList } from '@/lib/hooks/useServerList';
import type { Endpoint } from '@/types/endpoint';

const LIST_CONFIG: ListQueryConfig = {
  filterKeys: ['instance_id', 'status', 'type'],
  numericFilterKeys: ['instance_id'],
  sortableColumns: ['name', 'type', 'status', 'created_at'],
  defaultSortBy: 'created_at',
  defaultSortOrder: 'desc',
};

const STATUS_OPTIONS = ['up', 'down', 'unknown'].map((s) => ({ value: s, label: s }));
const TYPE_OPTIONS = ['docker', 'kubernetes', 'agent'].map((t) => ({ value: t, label: t }));

const fetchInstances = () => api.instances.list();

function createdText(iso: string): { ago: string; full: string } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

const COLUMNS: ResourceColumn<Endpoint>[] = [
  {
    key: 'name', header: 'Name', sortable: true, title: (e) => e.name,
    cell: (e) => <Link to={`/resources/endpoints/${e.id}`} className="text-primary font-medium hover:underline">{e.name}</Link>,
  },
  {
    key: 'type', header: 'Type', sortable: true,
    cell: (e) => <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{e.type}</span>,
  },
  { key: 'status', header: 'Status', sortable: true, cell: (e) => <StatusBadge status={e.status} /> },
  {
    key: 'traefik', header: 'Traefik', hideBelow: 'md',
    cell: (e) => {
      const Icon = e.traefik_deployed ? CircleCheck : CircleMinus;
      return (
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${e.traefik_deployed ? 'text-status-success' : 'text-muted-foreground'}`}>
          <Icon className="size-3" aria-hidden="true" />
          {e.traefik_deployed ? 'Deployed' : 'Not deployed'}
        </span>
      );
    },
  },
  { key: 'instance', header: 'Instance', hideBelow: 'md', cell: (e) => e.instance?.name ?? '—' },
  {
    key: 'url', header: 'URL', hideBelow: 'lg', title: (e) => e.url ?? '',
    cell: (e) => (e.url ? <span className="block max-w-xs truncate font-mono text-xs">{e.url}</span> : '—'),
  },
  {
    key: 'created_at', header: 'Created', sortable: true, hideBelow: 'md',
    cell: (e) => {
      const t = createdText(e.created_at);
      return t ? <time dateTime={e.created_at} title={t.full}>{t.ago}</time> : '—';
    },
  },
];
const ALL_KEYS = COLUMNS.map((c) => c.key);

export function EndpointsPage() {
  const { query, setSearch, setFilter, setSort, setPage, setLimit, clearFilters } = useListQuery(LIST_CONFIG, { limitStorageKey: 'endpoints.limit' });
  const { rows, total, loading, refreshing, error, refetch } = useServerList<Endpoint>('endpoints', query);
  const instancesP = usePolledData(fetchInstances);
  const [visible, toggleColumn] = usePersistedColumns('endpoints.columns', ALL_KEYS, ALL_KEYS);

  const filters = useMemo<ResourceFilterDef[]>(
    () => [
      { key: 'instance_id', label: 'Instance', allLabel: 'All instances', options: (instancesP.data ?? []).map((i) => ({ value: String(i.id), label: i.name })) },
      { key: 'status', label: 'Status', allLabel: 'All statuses', options: STATUS_OPTIONS },
      { key: 'type', label: 'Type', allLabel: 'All types', options: TYPE_OPTIONS },
    ],
    [instancesP.data],
  );

  const onPage = useCallback((p: number) => setPage(p, total), [setPage, total]);

  return (
    <div className="space-y-6">
      <PageHeader title="Endpoints" description="Docker endpoints discovered on your Portainer instances." />
      <ResourceList<Endpoint>
        columns={COLUMNS}
        visibleColumns={visible}
        onToggleColumn={toggleColumn}
        rows={rows}
        total={total}
        getRowKey={(e) => e.id}
        query={query}
        onSearch={setSearch}
        onFilter={setFilter}
        onSort={setSort}
        onPage={onPage}
        onLimit={setLimit}
        onClearFilters={clearFilters}
        filters={filters}
        searchLabel="Search endpoints"
        searchPlaceholder="Search by name or URL"
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        tableLabel="Endpoints"
        empty={{ title: 'No endpoints yet', description: 'Endpoints appear here after an instance syncs.' }}
      />
    </div>
  );
}
